import React from "react";
import logger from "utils/logger";
import {
  isChunkLoadError,
  isReloadPending,
  reloadOnceForNewVersion,
} from "utils/chunkReload";

/**
 * Fallback para erro de renderização.
 *
 * - Erro de chunk (versão nova publicada com a aba aberta) recarrega sozinho,
 *   uma vez, em vez de mostrar a tela de erro.
 * - Os demais vão para o logger (PostHog fora de localhost). Antes chamava
 *   `window.__COMPONENT_ERROR__`, que não existia: nenhum crash era reportado.
 * - `resetKey` (o pathname) limpa o erro ao navegar: um erro numa tela não
 *   prende o usuário nela até recarregar.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, reloading: false };
  }

  static getDerivedStateFromError() {
    // Já decidido em `vite:preloadError`: a página vai recarregar.
    return { hasError: true, reloading: isReloadPending() };
  }

  componentDidCatch(error, errorInfo) {
    if (isReloadPending() || (isChunkLoadError(error) && reloadOnceForNewVersion())) {
      if (!this.state.reloading) this.setState({ reloading: true });
      return;
    }
    logger.error("ErrorBoundary:", error, {
      componentStack: errorInfo?.componentStack,
    });
  }

  componentDidUpdate(prevProps, prevState) {
    // Só limpa um erro que JÁ existia antes da navegação. Sem o `prevState`,
    // uma página que quebra ao montar (mesmo commit em que a chave muda) era
    // resetada na hora, renderizava de novo e quebrava outra vez — dois
    // crashes reportados por um.
    if (
      prevState.hasError &&
      this.state.hasError &&
      prevProps.resetKey !== this.props.resetKey
    ) {
      this.setState({ hasError: false, reloading: false });
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    if (this.state.reloading) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background">
          <p className="text-muted-foreground">Atualizando para a versão mais recente…</p>
        </div>
      );
    }

    return (
      <div
        role="alert"
        className="min-h-screen flex items-center justify-center bg-background px-4"
      >
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-semibold text-foreground">
            Algo deu errado nesta tela
          </h1>
          <p className="mt-2 text-muted-foreground">
            Recarregar costuma resolver. Se continuar, volte ao painel e tente
            de novo daqui a pouco.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="min-h-[44px] px-5 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors"
            >
              Recarregar
            </button>
            <button
              type="button"
              onClick={() => {
                window.location.href = "/dash/user-dashboard";
              }}
              className="min-h-[44px] px-5 rounded-lg border border-border text-foreground font-medium hover:bg-muted transition-colors"
            >
              Ir para o painel
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
